//#region import libraries
import React, { useEffect, useState } from 'react';

// custom components
import { CustomSpinner, NewScreen, SearchBar, Title } from '_atoms';
import EmployeeRow from '_components/molecules/staff-management/employee-row';
import { useIsMobile } from '_utils/hooks';
import InviteMemberModal from '_components/molecules/staff-management/invite-member-modal';

// bootstrap
import { Container, Row, Col, Button, Alert, Modal, Form } from 'react-bootstrap';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectFirebaseUser } from '_redux/slices/main-screen/user';
import {
    fetchEmployeeList,
    updateEmployeeRole,
    deleteEmployee,
    resendInvitation,
    requireEmployeeMfa,
    sendPasswordReset,
    selectEmployeeList,
    selectStaffManagementStatus,
    selectStaffManagementError,
    selectStaffTotal,
    selectStaffHasMore,
    selectStaffPage,
    selectStaffPageSize,
} from '_redux/slices/profile/staff-management';
//#endregion

const StaffManagement = () => {
    // phones: tighter list card padding
    const isMobile = useIsMobile();

    //#region redux
    const dispatch = useDispatch();
    const firebaseUser = useSelector(selectFirebaseUser);
    const employeeList = useSelector(selectEmployeeList);
    const status = useSelector(selectStaffManagementStatus);
    const error = useSelector(selectStaffManagementError);
    const total = useSelector(selectStaffTotal);
    const hasMore = useSelector(selectStaffHasMore);
    const page = useSelector(selectStaffPage);
    const pageSize = useSelector(selectStaffPageSize);
    //#endregion

    //#region local state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    //#endregion

    //#region useEffect
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        setSelectedIds(new Set());
        dispatch(fetchEmployeeList({ limit: pageSize, skip: 0, search: debouncedSearch }));
    }, [debouncedSearch]);

    useEffect(() => {
        if (alert.show) {
            const timer = setTimeout(() => {
                setAlert({ show: false, message: '', variant: 'success' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [alert.show]);
    //#endregion

    //#region list helpers
    const loadEmployees = (skip = 0) => {
        setSelectedIds(new Set());
        dispatch(fetchEmployeeList({ limit: pageSize, skip, search: debouncedSearch }));
    };

    const selectableEmployees = employeeList.filter(
        (e) => !(firebaseUser && e.id === firebaseUser.uid)
    );
    const allSelectableSelected =
        selectableEmployees.length > 0 &&
        selectableEmployees.every((e) => selectedIds.has(e.id));
    //#endregion

    //#region pagination
    const handleNextPage = () => {
        loadEmployees((page + 1) * pageSize);
    };

    const handlePrevPage = () => {
        loadEmployees(Math.max(0, (page - 1) * pageSize));
    };

    const totalPages = Math.ceil(total / pageSize);
    //#endregion

    //#region action handlers
    const handleRoleChange = async (employeeId, role) => {
        try {
            await dispatch(updateEmployeeRole({ employeeId, role })).unwrap();
            setAlert({ show: true, message: 'Role updated successfully.', variant: 'success' });
        } catch (e) {
            setAlert({ show: true, message: e.message || 'Failed to update role.', variant: 'danger' });
            loadEmployees(page * pageSize);
        }
    };

    const handleDelete = async (employeeId) => {
        try {
            await dispatch(deleteEmployee(employeeId)).unwrap();
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(employeeId);
                return next;
            });
            setAlert({ show: true, message: 'Member removed successfully.', variant: 'success' });
            loadEmployees(page * pageSize);
        } catch (e) {
            setAlert({ show: true, message: e.message || 'Failed to remove member.', variant: 'danger' });
        }
    };

    const handleResendInvite = async (employeeId) => {
        try {
            await dispatch(resendInvitation(employeeId)).unwrap();
            setAlert({ show: true, message: 'Invitation resent successfully.', variant: 'success' });
        } catch (e) {
            setAlert({ show: true, message: e.message || 'Failed to resend invitation.', variant: 'danger' });
        }
    };

    const handleRequireMfa = async (employeeId) => {
        try {
            const result = await dispatch(requireEmployeeMfa(employeeId)).unwrap();
            setAlert({
                show: true,
                message: result?.message || 'MFA requirement updated.',
                variant: 'success',
            });
            loadEmployees(page * pageSize);
        } catch (e) {
            setAlert({ show: true, message: e.message || 'Failed to require password reset and MFA.', variant: 'danger' });
        }
    };

    const handleSendPasswordReset = async (email) => {
        try {
            await dispatch(sendPasswordReset(email)).unwrap();
            setAlert({
                show: true,
                message: `Password reset email sent to ${email}.`,
                variant: 'success',
            });
        } catch (e) {
            setAlert({ show: true, message: e.message || 'Failed to send password reset email.', variant: 'danger' });
        }
    };

    const handleInvited = (message) => {
        setAlert({
            show: true,
            message: message || 'Invitation sent successfully.',
            variant: 'success',
        });
        loadEmployees(page * pageSize);
    };

    const handleSelectToggle = (employeeId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(employeeId)) next.delete(employeeId);
            else next.add(employeeId);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (allSelectableSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectableEmployees.map((e) => e.id)));
        }
    };

    const handleBulkDelete = async () => {
        setShowBulkDeleteConfirm(false);
        const ids = [...selectedIds];
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
            try {
                await dispatch(deleteEmployee(id)).unwrap();
                successCount++;
            } catch (e) {
                failCount++;
            }
        }

        setSelectedIds(new Set());
        loadEmployees(page * pageSize);

        if (failCount === 0) {
            setAlert({ show: true, message: `${successCount} member(s) removed successfully.`, variant: 'success' });
        } else {
            setAlert({ show: true, message: `${successCount} removed, ${failCount} failed.`, variant: 'warning' });
        }
    };
    //#endregion

    return (
        <NewScreen>
            {/* Title & Subtitle */}
            <Row>
                <Col>
                    <Title
                        subtitle={'Staff Management'}
                        description={'Invite team members and manage their roles and access'}
                    />
                </Col>
            </Row>

            {alert.show && (
                <Alert variant={alert.variant} className="mt-3">
                    {alert.message}
                </Alert>
            )}

            <Row className="mt-4 align-items-center">
                <Col xs={12} md={6} className="mb-2 mb-md-0">
                    <SearchBar
                        placeholder="Search by name or email"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        isTimeline={false}
                        style={{ minWidth: '100%', maxWidth: '100%' }}
                    />
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-md-end">
                    <Button variant="primary" onClick={() => setShowInviteModal(true)}>
                        Invite Member
                    </Button>
                </Col>
            </Row>

            {status === 'loading' && employeeList.length === 0 ? (
                <Row className="justify-content-md-center mt-5">
                    <CustomSpinner />
                </Row>
            ) : (
                <Container
                    style={{
                        boxShadow: '0px 4px 8px 2px #9E9E9E',
                        borderRadius: '8px',
                        padding: isMobile ? '8px' : '24px',
                        width: '100%',
                        marginBottom: '40px',
                        marginTop: '20px',
                        opacity: status === 'loading' ? 0.6 : 1,
                    }}
                >
                    {status === 'failed' && error && (
                        <Alert variant="danger">{error}</Alert>
                    )}

                    {employeeList.length === 0 && status !== 'loading' ? (
                        <p className="text-muted text-center my-4">
                            {debouncedSearch
                                ? `No team members match "${debouncedSearch}".`
                                : 'No team members found. Use "Invite Member" to add your first team member.'}
                        </p>
                    ) : (
                        <>
                            {selectableEmployees.length > 0 && (
                                <div className="d-flex align-items-center justify-content-between mb-3 p-2 border rounded bg-light">
                                    <Form.Check
                                        type="checkbox"
                                        label={allSelectableSelected ? 'Deselect all' : 'Select all'}
                                        checked={allSelectableSelected}
                                        onChange={handleSelectAll}
                                        disabled={status === 'loading'}
                                    />
                                    {selectedIds.size > 0 && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => setShowBulkDeleteConfirm(true)}
                                            disabled={status === 'loading'}
                                        >
                                            Delete Selected ({selectedIds.size})
                                        </Button>
                                    )}
                                </div>
                            )}

                            <ul className="list-group list-group-flush">
                                {employeeList.map((employee) => (
                                    <EmployeeRow
                                        key={employee.id}
                                        employee={employee}
                                        isCurrentUser={firebaseUser && employee.id === firebaseUser.uid}
                                        onRoleChange={handleRoleChange}
                                        onDelete={handleDelete}
                                        onResendInvite={handleResendInvite}
                                        onRequireMfa={handleRequireMfa}
                                        onSendPasswordReset={handleSendPasswordReset}
                                        loading={status === 'loading'}
                                        selected={selectedIds.has(employee.id)}
                                        onSelectToggle={handleSelectToggle}
                                    />
                                ))}
                            </ul>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center gap-2 mt-3 pt-3 border-top">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={page === 0 || status === 'loading'}
                                        onClick={handlePrevPage}
                                    >
                                        Previous
                                    </Button>
                                    <span className="text-muted small text-center">
                                        Page {page + 1} of {totalPages} ({total} members)
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={!hasMore || status === 'loading'}
                                        onClick={handleNextPage}
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Container>
            )}

            {/* invite modal */}
            <InviteMemberModal
                show={showInviteModal}
                onHide={() => setShowInviteModal(false)}
                onInvited={handleInvited}
            />

            {/* bulk delete confirmation modal */}
            <Modal show={showBulkDeleteConfirm} onHide={() => setShowBulkDeleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Remove {selectedIds.size} Member{selectedIds.size !== 1 ? 's' : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to remove <strong>{selectedIds.size}</strong> team member{selectedIds.size !== 1 ? 's' : ''}?
                    </p>
                    <p className="text-muted small">
                        This will delete their accounts and remove their access to the portal.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBulkDeleteConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleBulkDelete}>
                        Remove {selectedIds.size} Member{selectedIds.size !== 1 ? 's' : ''}
                    </Button>
                </Modal.Footer>
            </Modal>
        </NewScreen>
    );
};

export default StaffManagement;
